import { useMemo, useState } from 'react';
import { Card, Icon, IconButton, ProgressRing } from '../ds';
import { Avatar, EmptyState, Eyebrow, Mono, Screen, SectionHeading } from '../components/primitives';
import { NoteRow, NotesSheet } from '../components/NotesSheet';
import * as api from '../lib/db';
import { useFamily } from '../state/FamilyContext';
import { entryTone, roleTone } from '../lib/labels';
import { entryTouchesDay, isOpenSleep, totalSleepMinutes } from '../lib/entries';
import {
  ageLabel,
  ageLabelEn,
  clockStringTo12,
  formatDateLine,
  formatDurationCompact,
  formatRelative,
  greetingJa,
  greetingEn,
  minutesBetween,
  parseISODate,
} from '../lib/time';
import type { Child, Member, ParentRole } from '../lib/types';
import type { ScreenKey } from '../components/AppShell';

/**
 * Home / today.
 *
 * The handoff screen: who is on duty, how the baby's day is going, what is
 * still unpacked for kindergarten, the note thread between the two parents,
 * and who logged what. Everything here answers "what do I need to know if I
 * just took over?".
 */

export function HomeScreen({
  onNavigate,
  onOpenSettings,
}: {
  onNavigate: (key: ScreenKey) => void;
  onOpenSettings: () => void;
}) {
  const {
    familyId,
    family,
    members,
    me,
    children,
    entries,
    openSleeps,
    notes,
    activity,
    prepItems,
    prepDay,
    today,
  } = useFamily();

  const now = new Date();
  const [noteDraft, setNoteDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  const baby = children.find((c) => c.kind === 'baby') ?? children[0] ?? null;
  const kid = children.find((c) => c.kind === 'kid') ?? null;

  const memberByUid = useMemo(() => new Map(members.map((m) => [m.uid, m])), [members]);
  const parents = useMemo(() => members.filter((m) => m.role !== 'viewer'), [members]);
  const mom = parents.find((m) => m.role === 'mom') ?? null;
  const dad = parents.find((m) => m.role === 'dad') ?? null;

  // ---- today's snapshot ----------------------------------------------------
  /**
   * Keyed on `today` (the local day, which the family context keeps current)
   * rather than on `now`: a fresh Date every render would defeat the memo, and
   * depending on the day means the list also rolls over at midnight if the app
   * was left open overnight — which, in this app, it will be.
   */
  const todaysEntries = useMemo(
    () =>
      entries.filter(
        (e) => entryTouchesDay(e, parseISODate(today)) && (!baby || e.childId === baby.id),
      ),
    [entries, baby?.id, today],
  );

  const feeds = todaysEntries.filter((e) => e.type === 'feed' || e.type === 'solid');
  const diapers = todaysEntries.filter((e) => e.type === 'diaper');
  const sleepMinutes = totalSleepMinutes(todaysEntries, now);
  const lastFeed = feeds[0] ?? null;

  const runningSleep = openSleeps.find(isOpenSleep) ?? null;

  // ---- prep --------------------------------------------------------------
  const prepForKid = useMemo(
    () => prepItems.filter((p) => !kid || !p.childId || p.childId === kid.id),
    [prepItems, kid],
  );
  const prepDone = prepForKid.filter((p) => prepDay.checks[p.id]).length;
  const prepTotal = prepForKid.length;
  const prepLeft = prepTotal - prepDone;

  const submitNote = async () => {
    if (!familyId || !me || !noteDraft.trim()) return;
    setSending(true);
    try {
      await api.addNote(familyId, me, noteDraft);
      setNoteDraft('');
    } finally {
      setSending(false);
    }
  };

  const setDuty = async (role: ParentRole) => {
    if (!familyId || !me || family?.onDuty === role) return;
    await api.setOnDuty(familyId, role, me);
  };

  const myName = me?.name ?? '';

  return (
    <Screen>
      {/* ---- header ---- */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          marginBottom: 16,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Eyebrow>{formatDateLine(now)}</Eyebrow>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 'var(--text-2xl)',
              margin: '3px 0 0',
            }}
          >
            {greetingJa(now)}
            {myName ? `、${myName}` : ''}
          </h1>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 2 }}>
            {greetingEn(now)}
            {me?.nameEn ? `, ${me.nameEn}` : ''}
          </div>
        </div>
        <IconButton icon="Settings" label="設定" variant="soft" round onClick={onOpenSettings} />
      </div>

      {/* ---- on duty ---- */}
      <Card padding="md">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            marginBottom: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <Eyebrow>On duty · いま担当</Eyebrow>
            {family?.handoverAt ? (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 3 }}>
                交代は <Mono>{family.handoverAt}</Mono> · Handover at {clockStringTo12(family.handoverAt)}
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          {(['mom', 'dad'] as ParentRole[]).map((role) => {
            const member = role === 'mom' ? mom : dad;
            const active = family?.onDuty === role;
            const tone = roleTone(role);
            return (
              <button
                key={role}
                type="button"
                aria-pressed={active}
                onClick={() => setDuty(role)}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  padding: '9px 11px',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  border: `1.5px solid ${active ? tone.border : 'var(--border)'}`,
                  background: active ? tone.bg : 'var(--surface)',
                  transition: 'all .18s var(--ease-standard)',
                }}
              >
                <Avatar initial={member?.initial ?? (role === 'mom' ? 'マ' : 'パ')} color={tone.solid} />
                <span style={{ textAlign: 'left', minWidth: 0 }}>
                  <span
                    style={{
                      display: 'block',
                      fontWeight: 700,
                      fontSize: 'var(--text-sm)',
                      color: active ? tone.text : 'var(--text-muted)',
                    }}
                  >
                    {member?.name ?? (role === 'mom' ? 'ママ' : 'パパ')}
                  </span>
                  <span style={{ display: 'block', fontSize: 10, color: 'var(--text-subtle)' }}>
                    {member?.nameEn ?? (role === 'mom' ? 'Mom' : 'Dad')}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {!dad || !mom ? (
          <div
            className="cjk"
            style={{
              marginTop: 12,
              fontSize: 'var(--text-xs)',
              color: 'var(--text-muted)',
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
            }}
          >
            <span style={{ color: 'var(--accent)', flex: 'none', marginTop: 2 }}>
              <Icon name="UserPlus" size={14} />
            </span>
            <span>
              もう一人の親がまだ参加していません。設定から招待コードを共有できます。
            </span>
          </div>
        ) : null}
      </Card>

      {/* ---- baby snapshot ---- */}
      {baby ? (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 'var(--space-3)',
              margin: '20px 0 10px',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <h3
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: 'var(--text-lg)',
                  margin: 0,
                }}
              >
                {baby.name}の一日
              </h3>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                {baby.nameEn || baby.name} · {ageLabelEn(baby.birthDate, now)}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onNavigate('log')}
              style={{
                border: 'none',
                background: 'transparent',
                color: 'var(--text-link)',
                fontSize: 'var(--text-sm)',
                fontWeight: 700,
                cursor: 'pointer',
                padding: 0,
                flex: 'none',
              }}
            >
              記録を見る
            </button>
          </div>

          <div style={{ display: 'flex', gap: 9 }}>
            <SnapshotTile
              icon="Milk"
              tone={entryTone('feed')}
              value={String(feeds.length)}
              unit="回"
              label={lastFeed ? `授乳 · ${formatRelative(lastFeed.at, now)}` : '授乳'}
            />
            <SnapshotTile
              icon="Baby"
              tone={entryTone('diaper')}
              value={String(diapers.length)}
              unit="回"
              label="おむつ"
            />
            <SnapshotTile
              icon="Moon"
              tone={entryTone('sleep')}
              value={sleepMinutes >= 60 ? (sleepMinutes / 60).toFixed(1) : String(Math.round(sleepMinutes))}
              unit={sleepMinutes >= 60 ? 'h' : 'm'}
              label={runningSleep ? 'ねんね · 記録中' : 'ねんね'}
            />
          </div>

          {/* An open nap is the one thing you most need to see on handover. */}
          {runningSleep ? (
            <Card padding="sm" style={{ marginTop: 9, background: 'var(--brand-subtle)', borderColor: 'transparent' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ color: 'var(--brand)', flex: 'none' }}>
                  <Icon name="Moon" size={17} />
                </span>
                <div style={{ flex: 1, fontSize: 'var(--text-sm)' }}>
                  ねんね記録中
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    <Mono>{formatRelative(runningSleep.at, now)}</Mono> から ·{' '}
                    {formatDurationCompact(minutesBetween(runningSleep.at, now))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onNavigate('log')}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--text-link)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  おわりを記録
                </button>
              </div>
            </Card>
          ) : null}
        </>
      ) : null}

      {/* ---- prep progress ---- */}
      {prepTotal > 0 ? (
        <div style={{ marginTop: 20 }}>
          <Card padding="md" interactive onClick={() => onNavigate('prep')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
              <ProgressRing
                value={prepDone}
                max={prepTotal}
                size={64}
                thickness={8}
                label={`${prepDone}/${prepTotal}`}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Eyebrow>Kindergarten · 通園準備</Eyebrow>
                <h3
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontWeight: 700,
                    fontSize: 'var(--text-lg)',
                    margin: '3px 0 2px',
                  }}
                >
                  {kid ? `${kid.name}の準備` : '通園準備'}
                </h3>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                  {prepLeft === 0 ? 'ぜんぶ準備できました · All packed' : `あと${prepLeft}つ · ${prepLeft} left to pack`}
                </div>
              </div>
              <span style={{ color: 'var(--text-subtle)', flex: 'none' }}>
                <Icon name="ChevronRight" size={20} />
              </span>
            </div>

            <div style={{ display: 'flex', gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
              {prepForKid.slice(0, 4).map((item) => {
                const done = !!prepDay.checks[item.id];
                return (
                  <span
                    key={item.id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '5px 10px',
                      borderRadius: 'var(--radius-pill)',
                      fontSize: 11,
                      fontWeight: 600,
                      background: done ? 'var(--sage-100)' : 'var(--sand-200)',
                      color: done ? 'var(--sage-600)' : 'var(--sand-700)',
                    }}
                  >
                    <Icon name={item.icon} size={13} />
                    {item.label}
                  </span>
                );
              })}
            </div>
          </Card>
        </div>
      ) : null}

      {/* ---- handoff note ---- */}
      <div style={{ marginTop: 20 }}>
        <SectionHeading note="Handoff note">引き継ぎメモ</SectionHeading>
        <Card padding="md">
          {notes.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
              {notes.slice(0, 4).map((note) => (
                <NoteRow key={note.id} note={note} author={memberByUid.get(note.byUid)} now={now} />
              ))}
              {notes.length > 4 ? (
                <button
                  type="button"
                  onClick={() => setNotesOpen(true)}
                  className="cjk"
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: '4px 0 0',
                    fontSize: 'var(--text-xs)',
                    color: 'var(--brand)',
                    cursor: 'pointer',
                    textAlign: 'center',
                  }}
                >
                  ぜんぶ見る · See all ({notes.length})
                </button>
              ) : null}
            </div>
          ) : (
            <EmptyState style={{ padding: '6px 0 14px' }}>
              まだメモはありません。ひとことだけでも残しておくと助かります。
            </EmptyState>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submitNote();
            }}
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              borderTop: '1px solid var(--border)',
              paddingTop: 11,
            }}
          >
            <input
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="メモを残す… Leave a note"
              maxLength={500}
              aria-label="引き継ぎメモ"
              style={{
                flex: 1,
                minWidth: 0,
                border: 'none',
                background: 'transparent',
                fontSize: 'var(--text-sm)',
                color: 'var(--text)',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              aria-label="メモを送る"
              disabled={!noteDraft.trim() || sending}
              style={{
                flex: 'none',
                width: 34,
                height: 34,
                borderRadius: '50%',
                border: 'none',
                background: noteDraft.trim() ? 'var(--brand)' : 'var(--sand-300)',
                color: 'var(--brand-on)',
                cursor: noteDraft.trim() ? 'pointer' : 'not-allowed',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background var(--duration-fast) var(--ease-standard)',
              }}
            >
              <Icon name="Send" size={16} />
            </button>
          </form>
        </Card>
      </div>

      {/* ---- recent activity ---- */}
      <div style={{ marginTop: 20 }}>
        <SectionHeading note="Recent · 誰が何を">さいきんの記録</SectionHeading>
        <Card padding="none" style={{ overflow: 'hidden' }}>
          {activity.length ? (
            activity.slice(0, 8).map((item, i, all) => {
              const author = memberByUid.get(item.byUid);
              const tone = roleTone(item.byRole);
              return (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 11,
                    padding: '12px 15px',
                    borderBottom: i === all.length - 1 ? 'none' : '1px solid var(--border)',
                  }}
                >
                  <span
                    style={{
                      flex: 'none',
                      width: 32,
                      height: 32,
                      borderRadius: 'var(--radius-sm)',
                      background: tone.bg,
                      color: tone.fg,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name={item.icon} size={16} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text)', fontWeight: 500 }}>
                      {item.action}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
                      {author?.name ?? (item.byRole === 'mom' ? 'ママ' : 'パパ')}
                    </div>
                  </div>
                  <Mono style={{ fontSize: 'var(--text-xs)', color: 'var(--text-subtle)', flex: 'none' }}>
                    {formatRelative(item.at, now)}
                  </Mono>
                </div>
              );
            })
          ) : (
            <EmptyState icon={<Icon name="Baby" size={22} />}>
              まだ記録がありません。＋ボタンから最初の記録をどうぞ。
            </EmptyState>
          )}
        </Card>
      </div>

      <NotesSheet
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        notes={notes}
        memberByUid={memberByUid}
        now={now}
      />
    </Screen>
  );
}

function SnapshotTile({
  icon,
  tone,
  value,
  unit,
  label,
}: {
  icon: string;
  tone: { bg: string; fg: string };
  value: string;
  unit: string;
  label: string;
}) {
  return (
    <Card padding="sm" style={{ flex: 1, minWidth: 0 }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 34,
          height: 34,
          borderRadius: 11,
          background: tone.bg,
          color: tone.fg,
        }}
      >
        <Icon name={icon} size={18} />
      </span>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
          fontSize: 'var(--text-xl)',
          marginTop: 9,
          lineHeight: 1,
        }}
      >
        {value}
        <span style={{ fontSize: 11, color: 'var(--text-subtle)', marginLeft: 2 }}>{unit}</span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{label}</div>
    </Card>
  );
}

/** Exported for the settings screen's member list. */
export function memberLabel(member: Member | undefined, role: ParentRole): string {
  return member?.name ?? (role === 'mom' ? 'ママ' : 'パパ');
}

export function childLabel(child: Child | null, now = new Date()): string {
  return child ? `${child.name} · ${ageLabel(child.birthDate, now)}` : '';
}
